import { Skill } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { createApiHeadersGetter } from '../utils/api-headers';
import { SKILLS_URL } from '../config/api-config';

interface APIError {
  message: string;
  code?: string;
  details?: any;
  status?: number;
}

export class ApiError extends Error {
  status: number;
  details?: any;

  constructor(message: string, status: number, details?: any) {
    super(message);
    this.status = status;
    this.details = details;
    this.name = 'ApiError';
  }
}

export const useSkillsApi = () => {
    const { getAuthToken } = useAuth();
    const getHeaders = createApiHeadersGetter(getAuthToken);
  
    const getSkills = async (getAllSkills: boolean = false): Promise<Skill[]> => {
      try {
        const headers = await getHeaders();
        const response = await fetch(`${SKILLS_URL}?getAllSkills=${getAllSkills}`,
          {
            method: 'GET',
            headers
          }
        );
        if (!response.ok) {
          throw new Error('Failed to fetch skills');
        }
        return response.json();
      } catch (error) {
        console.error('Error fetching Skills:', error);
        throw error;
      }
    };

    const getSkill = async (id: string): Promise<Skill> => {
      try {
        const headers = await getHeaders();
        const response = await fetch(`${SKILLS_URL}/${id}`,
          {
            method: 'GET',
            headers
          }
        );
        console.log('[getSkill] Response:', response);
        
        if (!response.ok) {
          let errorMessage = 'Failed to fetch Skill';
          let errorDetails;
          
          try {
            // Try to parse error response as JSON
            const errorData: APIError = await response.json();
            errorMessage = errorData.message || errorMessage;
            errorDetails = errorData.details;
          } catch (parseError) {
            // If JSON parsing fails, use status text
            errorMessage = response.statusText || errorMessage;
          }
          
          throw new ApiError(errorMessage, response.status, errorDetails);
        }
  
        return await response.json();
      } catch (error: any) {
        console.error('Error fetching Skill:', error);
        // If it's already an ApiError, rethrow it
        if (error instanceof ApiError) {
          throw error;
        }
        // Otherwise wrap it in an ApiError
        throw new ApiError(error.message || 'Unknown error occurred', 500);
      }
    };

    const createSkill = async (skill: Omit<Skill, 'id'>): Promise<Skill> => {
      try {
        const headers = await getHeaders();
        const response = await fetch(SKILLS_URL,
          {
            method: 'POST',
            headers,
            body: JSON.stringify(skill),
          }
        );
  
        if (!response.ok) {
          console.log('[createSkill] Error creating skill response:', response);
          try {
            const errorData: APIError = await response.json();
            console.log('[createSkill] Error creating skill errorData:', errorData);
            throw new Error(errorData.message || 'Failed to create skill');
          } catch (parseError) {
            console.log('[createSkill] Error parsing JSON:', String(parseError));
            throw new Error(String(parseError));
          }
        }
        return response.json();
      } catch (error) {
        console.error('Error creating Skill:', error);
        throw error;
      }
    };
  
    const updateSkill = async (skill: Partial<Skill>): Promise<Skill> => {
      try {
        const headers = await getHeaders();
        const response = await fetch(`${SKILLS_URL}/${skill.skillId}`,
          {
            method: 'PUT',
            headers,
            body: JSON.stringify(skill),
          }
        );
        if (!response.ok) {
          throw new Error('Failed to update skill');
        }
        return response.json();
      } catch (error) {
        console.error('Error updating Skill:', error);
        throw error;
      }
    };
  
    const deleteSkill = async (id: string): Promise<void> => {
      try {
        const headers = await getHeaders();
        const response = await fetch(`${SKILLS_URL}/${id}`,
          {
            method: 'DELETE',
            headers
          }
        );
        if (!response.ok) {
          throw new Error('Failed to delete Skill');
        }
        return;
      } catch (error) {
        console.error('Error deleting Skill:', error);
        throw error;
      }
    };

    const testSkill = async (id: string, parameters: any): Promise<any> => {
      try {
        console.log(parameters);
        const headers = await getHeaders();
        const response = await fetch(`${SKILLS_URL}/${id}/test`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify(parameters),
          }
        );
        
        // Always try to parse the response as JSON, regardless of success or failure
        const data = await response.json().catch(err => {
          console.error('Error parsing response:', err);
          return { error: 'Failed to parse response' };
        });
        
        // Even if the response is not OK, return the exact data from the API
        // This ensures the raw API response body is passed back to the UI
        if (!response.ok) {
          console.error('Test skill failed with status:', response.status);
          console.error('Error response data:', data);
          return data; // Return the raw API response body
        }
        
        // Return the data exactly as received from the API, without transforming it
        return data;
      } catch (error) {
        console.error('Error testing Skill:', error);
        // For network errors or other exceptions, create an error object
        return {
          error: true,
          message: error instanceof Error ? error.message : 'Unknown error occurred'
        };
      }
    };
  
    return {
      getSkills,
      getSkill,
      createSkill,
      updateSkill,
      deleteSkill,
      testSkill
    };
  };